import { FIGURE_SOURCES, type FigureName, type Freshness } from '@pulse/contracts';
import * as React from 'react';

import { AlertIcon } from '@/components/pulse/icons';

/**
 * SHL-3's warning above every page: the last collection is more than 90 minutes old, or a source
 * failed in it. Nothing shows when the data is fresh and complete.
 */
export function FreshnessBanner({ freshness }: { readonly freshness: Freshness | null }): React.ReactElement | null {
  if (freshness === null) return null;
  const lines = [
    ...(freshness.stale
      ? [
          freshness.lastCollectedAt === null
            ? 'Pulse has not collected any data yet.'
            : `The data was last refreshed ${freshness.refreshedLabel ?? ''}, more than 90 minutes ago.`,
        ]
      : []),
    ...freshness.failures.map((failure) => `${failure.message}.`),
  ];
  if (lines.length === 0) return null;
  return (
    <div className="banner" role="status">
      <AlertIcon />
      <div className="stack" style={{ gap: 2 }}>
        {lines.map((line) => (
          <span key={line}>{line}</span>
        ))}
      </div>
    </div>
  );
}

/** Every page's quiet foot line: when its data was last refreshed (SHL-3). */
export function RefreshedLine({ freshness }: { readonly freshness: Freshness | null }): React.ReactElement | null {
  if (freshness === null) return null;
  return (
    <p className="refreshed">
      {freshness.refreshedLabel === null ? 'Not refreshed yet.' : `Refreshed ${freshness.refreshedLabel}.`} From GitHub, Teamwork and
      Talos&apos;s run log, hourly.
    </p>
  );
}

/** Whether a figure depends on a source that failed in the last collection. */
export function isIncomplete(figure: FigureName, freshness: Freshness | null): boolean {
  if (freshness === null) return false;
  const sources: ReadonlyArray<string> = FIGURE_SOURCES[figure];
  return freshness.failures.some((failure) => sources.includes(failure.source));
}

/** Beside a figure computed from a source that failed: never shown as complete (SHL-3). */
export function IncompleteMark({ figure, freshness }: { readonly figure: FigureName; readonly freshness: Freshness | null }): React.ReactElement | null {
  if (!isIncomplete(figure, freshness)) return null;
  const sources: ReadonlyArray<string> = FIGURE_SOURCES[figure];
  const reasons = (freshness?.failures ?? []).filter((failure) => sources.includes(failure.source)).map((failure) => failure.message);
  return (
    <span className="chip c-warn" title={reasons.join('. ')}>
      Incomplete
    </span>
  );
}

/**
 * A number, or, when Pulse has no data for it, "Not available" with the reason. Never a zero
 * standing in for a missing value (SHL-002).
 */
export function FigureValue({ value, unavailableReason }: { readonly value: number | string | null; readonly unavailableReason: string }): React.ReactElement {
  if (value !== null) return <>{value}</>;
  return (
    <span className="unavailable" title={unavailableReason}>
      Not available<span className="unavailable-reason">{unavailableReason}</span>
    </span>
  );
}
